import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { AuthService } from '../auth/services/auth.service';
import { ContactService, ContactStatus } from './contact.service';
import { PhoneSetupService } from './phone-setup.service';

export interface SetupTask {
  id: string;
  label: string;
  sublabel?: string;
  done: boolean;
  /** Opens whatever finishes the task. */
  action: () => void;
}

export function isStaffRole(roles: string[]): boolean {
  return roles
    .map((r) => String(r).toUpperCase())
    .some((r) => r.includes('ADMIN') || r.includes('TRAINER') || r.includes('SUPPORT'));
}

/** Pending "finish setting up" items for the signed-in end user. Add new tasks in `buildTasks`. */
@Injectable({ providedIn: 'root' })
export class SetupTasksService {
  private readonly tasksSubject = new BehaviorSubject<SetupTask[]>([]);
  readonly tasks$: Observable<SetupTask[]> = this.tasksSubject.asObservable();
  private readonly statusSubject = new BehaviorSubject<ContactStatus | null>(null);
  readonly status$ = this.statusSubject.asObservable();
  private lastUserId: string | null = null;

  constructor(
    private auth: AuthService,
    private contact: ContactService,
    private phoneSetup: PhoneSetupService
  ) {
    this.auth.currentUser$.subscribe(() => this.onUserChange());
    this.phoneSetup.completed$.subscribe(() => this.refresh());
  }

  get tasks(): SetupTask[] {
    return this.tasksSubject.value;
  }

  get userId(): string | null {
    return this.auth.currentUser?.id ?? null;
  }

  get eligible(): boolean {
    return !!this.auth.getToken() && !!this.auth.currentUser && !isStaffRole(this.auth.getRoles());
  }

  refresh(): void {
    if (!this.eligible) {
      this.tasksSubject.next([]);
      this.statusSubject.next(null);
      return;
    }
    this.contact.getStatus().subscribe({
      next: (status) => {
        if (!this.eligible) return;
        this.statusSubject.next(status);
        this.tasksSubject.next(this.buildTasks(status));
      },
      error: () => {
        /* keep the last known state; never block the app */
      }
    });
  }

  openVerification(): void {
    const s = this.statusSubject.value;
    void this.phoneSetup.openDialog({
      mode: 'setup',
      source: 'setup_prompt',
      countryIso2: s?.country_iso2,
      legacyE164: s?.legacy_whatsapp_e164,
      currentMasked: s?.whatsapp_e164_masked,
      pending: this.pendingIds(s)
    });
  }

  private onUserChange(): void {
    const id = this.auth.currentUser?.id ?? null;
    if (id === this.lastUserId) return;
    this.lastUserId = id;
    this.refresh();
  }

  private pendingIds(s: ContactStatus | null): string[] {
    return s ? this.buildTasks(s).filter((t) => !t.done).map((t) => t.id) : [];
  }

  private buildTasks(s: ContactStatus): SetupTask[] {
    const action = () => this.openVerification();
    const legacy = s.legacy_whatsapp_e164 && !s.whatsapp_verified ? maskE164(s.legacy_whatsapp_e164) : null;
    return [
      { id: 'country', label: 'Set your country', done: !!s.country_iso2, action },
      {
        id: 'whatsapp',
        label: 'Verify your WhatsApp number',
        sublabel: legacy ? `${legacy} isn't verified yet` : undefined,
        done: !!s.whatsapp_verified,
        action
      }
    ];
  }
}

/** "+919876543210" -> "+91 98•••210" */
export function maskE164(e164: string): string {
  const digits = e164.replace(/[^\d]/g, '');
  if (digits.length < 7) return e164;
  const cc = digits.length > 10 ? digits.slice(0, digits.length - 10) : digits.slice(0, 2);
  const rest = digits.slice(cc.length);
  return `+${cc} ${rest.slice(0, 2)}•••${rest.slice(-3)}`;
}
